
import { Component, Watch, Prop } from "vue-property-decorator";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import Vue from "vue";
import ThemeUpdatingView from "@/components/general/ThemeUpdatingView.vue";
import { TimePeriod } from "@/models/public-bot-stats/TimePeriod";
import { BotDuration } from "@/models/public-bot-stats/BotDuration";
import camelcaseKeys from "camelcase-keys";
import { yAxisInteractionLine } from "@/plugins/chartjs";

@Component
export default class TradesMadeChart extends ThemeUpdatingView {
  @Prop()
  public market!: string;

  @Prop({ default: null })
  public activeFor!: BotDuration;

  @Prop()
  public timePeriod!: TimePeriod;

  // 📦 Dates for each portfolio check-up
  public dateLabels!: Date[];

  // 📦📈 Chart which displays the bot balance over time
  private botBalanceChart: Chart | null = null;

  // 📦 The dataset for the buy trades
  private buyDataset: number[] = [];

  // 📦 The dataset for the sell trades
  private sellDataset: number[] = [];

  /**
   * 👂 Listener for changes in the selected time period
   */
  @Watch("timePeriod")
  private onSelectedTimePeriodChanged(val: TimePeriod, oldVal: TimePeriod) {
    this.fetchDataset();
  }

  /**
   * 👂 Listener for changes in the selected market
   */
  @Watch("market")
  private onSelectedMarketChange(val: string, oldVal: string) {
    this.fetchDataset();
  }

  /**
   * 👂 Listener for changes in the selected duration
   */
  @Watch("activeFor")
  private onSelectedDurationCHange(val: BotDuration, oldVal: BotDuration) {
    this.fetchDataset();
  }

  // 🚩 Flag for whether the chart's data is being loaded
  private isLoading: boolean = false;

  protected themeUpdate() {
    this.createChart();
  }

  protected mounted() {
    this.fetchDataset();
  }

  /**
   * 🚀 Fetches the dataset from the API
   */
  private fetchDataset() {
    if (!this.market || !this.timePeriod) {
      return;
    }

    this.isLoading = true;
    Vue.prototype.$api
      .get("/api/v1/stats/gettradedistribution", {
        market: this.market === "All" ? null : this.market,
        active_from: this.activeFor == -1 ? null : this.activeFor,
        period:
          this.timePeriod == TimePeriod.FromStart ||
          this.timePeriod > TimePeriod.Week
            ? "day"
            : "hour",
        start_time:
          this.timePeriod == -1 ? undefined : this.timePeriod,
      })
      .then((response: any) => camelcaseKeys(response.data, { deep: true }))
      .then((response: any) => {
        this.dateLabels = Object.keys(response).map(
          (value: String, index: Number, array: String[]) => {
            return new Date(
              Number(value) * 1000 - new Date().getTimezoneOffset() * 60
            );
          }
        );
        this.buyDataset = Object.values(response).map((datasetEntry: any) =>
          datasetEntry.buy ? datasetEntry.buy : 0
        );
        this.sellDataset = Object.values(response).map((datasetEntry: any) =>
          datasetEntry.sell ? datasetEntry.sell : 0
        );
        this.createChart();
      })
      .finally(() => (this.isLoading = false));
  }

  /**
   * 🎨 Draws a dynamic chart on the canvas which is responsible for displaying the portfolio
   */
  private createChart() {
    if (this.botBalanceChart == null) {
      let container: any = document.getElementById(
        "container-trades-made-chart"
      );
      this.botBalanceChart = new Chart(container, {
        type: "bar",
        data: {
          labels: this.dateLabels,
          datasets: this.formatChartDatasets(),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
          },
          scales: {
            x: this.getTimeScale(this.timePeriod),
            y: this.getYAxes(),
          },
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                boxWidth: 12,
                boxHeight: 3,
                font: { family: "Aeroport" },
              },
            },
            tooltip: {
              mode: "index",
              callbacks: {
                label: (context: any) => {
                  var index = context.dataIndex;
                  return (
                    " " +
                    context.dataset.label +
                    ": " +
                    Number(context.parsed.y) +
                    " (" +
                    (
                      ((context.dataset.label == "Buys"
                        ? this.buyDataset[index]
                        : this.sellDataset[index]) /
                        (this.buyDataset[index] + this.sellDataset[index])) *
                      100
                    ).toFixed(2) +
                    "%)"
                  );
                },
                afterBody: (context: any) => {
                  let index = context[0].dataIndex;
                  let ratio = this.buyDataset[index] / this.sellDataset[index];
                  return (
                    "Buy/Sell Ratio: " +
                    (this.buyDataset[index] > 0 && this.sellDataset[index] > 0
                      ? ratio.toFixed(2)
                      : "--")
                  );
                },
              },
            },
          },
        },
        plugins: [yAxisInteractionLine(this.$vuetify)],
      });
    } else {
      this.botBalanceChart.data.datasets = this.formatChartDatasets();
      this.botBalanceChart.data.labels = this.dateLabels;
      this.botBalanceChart.options.scales!!.x = this.getTimeScale(
        this.timePeriod
      );
      this.botBalanceChart.config.options!!.scales!!.y = this.getYAxes();
      this.botBalanceChart.update();
    }
  }

  /**
   * 🏗 Configures the format and properties for X-axis of the chart
   */
  private getTimeScale(timePeriod: TimePeriod): any {
    let configuration: any = {
      type: "timeseries",
      ticks: {
        color: this.$vuetify.theme.currentTheme.chartLabel,
      },
      grid: {
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
      stacked: true,
    };

    if (timePeriod == TimePeriod.Day) {
      configuration.time = {
        tooltipFormat: "dd/MM/yyyy HH:mm z",
        unit: "hour",
        color: this.$vuetify.theme.currentTheme.chartLabel,
      };
    }

    if ([TimePeriod.Week, TimePeriod.Month].includes(timePeriod)) {
      configuration.time = {
        tooltipFormat: "dd/MM/yyyy HH:mm z",
        unit: "day",
        color: this.$vuetify.theme.currentTheme.chartLabel,
      };
    }

    if (
      [
        TimePeriod.ThreeMonths,
        TimePeriod.SixMonths,
        TimePeriod.FromStart,
      ].includes(timePeriod)
    ) {
      configuration.time = {
        tooltipFormat: "dd/MM/yyyy",
        unit: "month",
        color: this.$vuetify.theme.currentTheme.chartLabel,
      };
    }
    return configuration;
  }

  /**
   * 🏗 Assembles the datasets that will be displayed
   */
  private formatChartDatasets(): any {
    return [
      {
        label: "Buys",
        data: this.buyDataset,
        backgroundColor: this.$vuetify.theme.currentTheme.chartBuyColor,
        borderColor: this.$vuetify.theme.currentTheme.chartBuyColor,
      },
      {
        label: "Sells",
        data: this.sellDataset,
        backgroundColor: this.$vuetify.theme.currentTheme.chartSellColor,
        borderColor: this.$vuetify.theme.currentTheme.chartSellColor,
      },
    ];
  }

  /**
   * 🔨 Calculates a dynamic y axis based on the provided dataset
   */
  private getYAxes(): any {
    return {
      ticks: {
        color: this.$vuetify.theme.currentTheme.chartLabel,
        font: { family: "Aeroport" },
      },
      grid: {
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
      stacked: true,
    };
  }
}
